import { useState, useMemo, ChangeEvent } from 'react'
import type { Book } from '@/types'
import { FIELD_LIMITS, truncateField, normalizeToArray, validateCategories } from '../../utils/validation'
import styles from './FetchReviewModal.module.css'

export interface APIResult {
    source: 'google' | 'openlibrary'
    description?: string
    categories?: string[] | string
    page_count?: number | string
    publisher?: string
}

export interface FetchResult {
    index: number
    currentBook: Book
    apiResults: APIResult[]
}

export interface UpdateFields {
    description: string
    category: string[]
    page_count: number | null
    publisher: string
}

export interface ApplyUpdate {
    index: number
    updatedFields: UpdateFields
}

interface FetchReviewModalProps {
    results: FetchResult[]
    onClose: () => void
    onApply: (updates: ApplyUpdate[]) => void
}

const getInitialEditedFields = (results: FetchResult[]) => {
    const initial = new Map<number, Map<string, APIResult>>()
    results.forEach(({ index, apiResults }) => {
        const sourceMap = new Map<string, APIResult>()
        if (apiResults && Array.isArray(apiResults)) {
            apiResults.forEach((res) => {
                const b = FIELD_LIMITS.books
                sourceMap.set(res.source, {
                    ...res,
                    description: truncateField(res.description, b.description),
                    categories: normalizeToArray(res.categories),
                    publisher: truncateField(res.publisher, b.publisher),
                })
            })
        }
        initial.set(index, sourceMap)
    })
    return initial
}

const getInitialSelectedSources = (results: FetchResult[]) => {
    const initial = new Map<number, Map<string, boolean>>()
    results.forEach(({ index, apiResults }) => {
        const sourceMap = new Map<string, boolean>()
        if (apiResults && Array.isArray(apiResults)) {
            apiResults.forEach((res) => {
                sourceMap.set(res.source, true)
            })
        }
        initial.set(index, sourceMap)
    })
    return initial
}

const mergeApiResults = (
    selectionsMap: Map<string, boolean> | undefined,
    infosMap: Map<string, APIResult> | undefined
): UpdateFields => {
    const final: UpdateFields = { description: '', category: [], page_count: null, publisher: '' }

    const olSelection = selectionsMap?.get('openlibrary')
    const olInfo = infosMap?.get('openlibrary')
    if (olSelection && olInfo) {
        final.description = olInfo.description || ''
        final.category = normalizeToArray(olInfo.categories)
        final.page_count = olInfo.page_count ? Number(olInfo.page_count) : null
        final.publisher = olInfo.publisher || ''
    }

    const gSelection = selectionsMap?.get('google')
    const gInfo = infosMap?.get('google')
    if (gSelection && gInfo) {
        if (gInfo.description) final.description = gInfo.description
        if (gInfo.categories && normalizeToArray(gInfo.categories).length > 0) {
            final.category = normalizeToArray(gInfo.categories)
        }
        if (gInfo.page_count) final.page_count = Number(gInfo.page_count)
        if (gInfo.publisher) final.publisher = gInfo.publisher
    }

    return final
}

export default function FetchReviewModal({ results, onClose, onApply }: Readonly<FetchReviewModalProps>) {
    const [editedInfos, setEditedInfos] = useState(() => getInitialEditedFields(results))
    const [selectedSources, setSelectedSources] = useState(() => getInitialSelectedSources(results))

    const [prevResults, setPrevResults] = useState(results)
    if (results !== prevResults) {
        setEditedInfos(getInitialEditedFields(results))
        setSelectedSources(getInitialSelectedSources(results))
        setPrevResults(results)
    }

    const handleFieldChange = (index: number, source: string, field: string, value: string) => {
        const b = FIELD_LIMITS.books
        const processingMap = new Map<string, (v: string) => string | string[] | number | null>([
            ['description', (v) => truncateField(v, b.description)],
            ['categories', (v) => normalizeToArray(v)],
            ['publisher', (v) => truncateField(v, b.publisher)],
        ])

        const processor = processingMap.get(field)
        const finalValue = processor ? processor(value) : value

        setEditedInfos((prev) => {
            const next = new Map(prev)
            const sourceMap = new Map(next.get(index) || [])
            const currentData = sourceMap.get(source) || ({} as APIResult)
            sourceMap.set(source, { ...currentData, [field]: finalValue })
            next.set(index, sourceMap)
            return next
        })
    }

    const toggleSourceSelection = (index: number, source: string) => {
        setSelectedSources((prev) => {
            const next = new Map(prev)
            const sourceMap = new Map(next.get(index) || [])
            sourceMap.set(source, !sourceMap.get(source))
            next.set(index, sourceMap)
            return next
        })
    }

    const toggleSelectAll = () => {
        const allSet = results.every(({ index, apiResults }) => apiResults?.every((res) => selectedSources.get(index)?.get(res.source)))

        const nextSelection = new Map<number, Map<string, boolean>>()
        results.forEach(({ index, apiResults }) => {
            const sourceMap = new Map<string, boolean>()
            apiResults?.forEach((res) => {
                sourceMap.set(res.source, !allSet)
            })
            nextSelection.set(index, sourceMap)
        })
        setSelectedSources(nextSelection)
    }

    const handleApply = () => {
        const b = FIELD_LIMITS.books
        const updates = results
            .map(({ index }) => {
                const indexSelections = selectedSources.get(index)
                if (!indexSelections) return null

                const hasAnySelected = Array.from(indexSelections.values()).some(Boolean)
                if (!hasAnySelected) return null

                const finalFields = mergeApiResults(indexSelections, editedInfos.get(index))

                return {
                    index,
                    updatedFields: {
                        description: truncateField(finalFields.description, b.description),
                        category: validateCategories(finalFields.category),
                        page_count: finalFields.page_count ? Number.parseInt(String(finalFields.page_count), 10) : null,
                        publisher: truncateField(finalFields.publisher, b.publisher),
                    },
                }
            })
            .filter((u): u is ApplyUpdate => u !== null)

        onApply(updates)
    }

    const stats = useMemo(() => {
        const available = results.reduce((acc, r) => acc + (r.apiResults?.length || 0), 0)
        let selected = 0
        selectedSources.forEach((sourceMap) => {
            sourceMap.forEach((isSelected) => {
                if (isSelected) selected++
            })
        })
        return { available, selected, allSelected: available > 0 && selected === available }
    }, [results, selectedSources])

    return (
        <div className={styles.fetchReviewOverlay}>
            <div className={styles.fetchReviewModal}>
                <div className={styles.fetchReviewHeader}>
                    <h3 className={styles.fetchReviewTitle}>
                        <span>🌐</span> Revisar Información Obtenida
                    </h3>
                    <button className={styles.fetchReviewClose} onClick={onClose} aria-label="Cerrar">
                        ✕
                    </button>
                </div>

                <div className={styles.reviewListHeader}>
                    <div className={styles.reviewCheckboxWrapper}>
                        <input type="checkbox" checked={stats.allSelected} onChange={toggleSelectAll} className={styles.reviewCheckbox} />
                    </div>
                    <span className={styles.reviewListHeaderLabel}>
                        Seleccionar todos los resultados ({stats.selected} de {stats.available})
                    </span>

                    <div className={styles.reviewColumnHeaders}>
                        <span className={styles.colHeader} data-testid="col-header">Descripción</span>
                        <span className={styles.colHeader} data-testid="col-header">Categoría</span>
                        <span className={styles.colHeader} data-testid="col-header">Págs</span>
                        <span className={styles.colHeader} data-testid="col-header">Editorial</span>
                    </div>
                </div>

                <div className={styles.fetchReviewBody}>
                    {results
                        .filter((r) => r.apiResults?.length > 0)
                        .map(({ index, currentBook, apiResults }) => (
                            <div key={index} className={styles.reviewGroup}>
                                <div className={`${styles.reviewRow} ${styles.currentRow}`}>
                                    <div className={`${styles.reviewCell} ${styles.empty}`} />
                                    <div className={`${styles.reviewCell} ${styles.mainInfo}`}>
                                        <div className={styles.bookTitle}>{currentBook.title}</div>
                                        <div className={styles.bookAuthor}>{currentBook.author}</div>
                                    </div>
                                    <div className={`${styles.reviewCell} ${styles.fieldValue}`} title={currentBook.description}>
                                        {currentBook.description ? `${currentBook.description.substring(0, 30)}...` : '(Sin descripción)'}
                                    </div>
                                    <div className={`${styles.reviewCell} ${styles.fieldValue}`}>
                                        {Array.isArray(currentBook.category)
                                            ? currentBook.category.join(', ')
                                            : currentBook.category || '(Sin categoría)'}
                                    </div>
                                    <div className={`${styles.reviewCell} ${styles.fieldValue}`}>{currentBook.page_count ? `${currentBook.page_count} págs` : '-'}</div>
                                    <div className={`${styles.reviewCell} ${styles.fieldValue}`}>{currentBook.publisher || '-'}</div>
                                </div>

                                {apiResults.map((res) => {
                                    const isSelected = !!selectedSources.get(index)?.get(res.source)
                                    const data = editedInfos.get(index)?.get(res.source) || ({} as APIResult)
                                    return (
                                        <div key={res.source} className={`${styles.reviewRow} ${styles.apiRow} ${isSelected ? styles.selected : ''}`} data-testid="api-row" data-selected={isSelected}>
                                            <div className={styles.reviewCheckboxWrapper}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleSourceSelection(index, res.source)}
                                                    className={styles.reviewCheckbox}
                                                    data-testid="review-checkbox"
                                                />
                                            </div>
                                            <div className={`${styles.reviewCell} ${styles.labelCell}`}>
                                                <span className={`${styles.sourceBadge} ${res.source === 'google' ? styles.google : styles.openlibrary}`}>
                                                    {res.source === 'google' ? 'Google Books' : 'Open Library'}
                                                </span>
                                            </div>
                                            <div className={styles.reviewCell}>
                                                <span className={styles.reviewLabel}>Descripción</span>
                                                <input
                                                    type="text"
                                                    className={styles.reviewInput}
                                                    value={data.description || ''}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        handleFieldChange(index, res.source, 'description', e.target.value)
                                                    }
                                                    placeholder="Descripción"
                                                    title={data.description || ''}
                                                    maxLength={FIELD_LIMITS.books.description}
                                                />
                                            </div>
                                            <div className={styles.reviewCell}>
                                                <span className={styles.reviewLabel}>Categoría</span>
                                                <input
                                                    type="text"
                                                    className={styles.reviewInput}
                                                    value={Array.isArray(data.categories) ? data.categories.join(', ') : data.categories || ''}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        handleFieldChange(index, res.source, 'categories', e.target.value)
                                                    }
                                                    placeholder="Categoría"
                                                    title={Array.isArray(data.categories) ? data.categories.join(', ') : data.categories || ''}
                                                    maxLength={FIELD_LIMITS.books.category}
                                                />
                                            </div>
                                            <div className={styles.reviewCell}>
                                                <span className={styles.reviewLabel}>Páginas</span>
                                                <input
                                                    type="number"
                                                    className={styles.reviewInput}
                                                    value={data.page_count || ''}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        handleFieldChange(index, res.source, 'page_count', e.target.value)
                                                    }
                                                    placeholder="Págs"
                                                />
                                            </div>
                                            <div className={styles.reviewCell}>
                                                <span className={styles.reviewLabel}>Editorial</span>
                                                <input
                                                    type="text"
                                                    className={styles.reviewInput}
                                                    value={data.publisher || ''}
                                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                        handleFieldChange(index, res.source, 'publisher', e.target.value)
                                                    }
                                                    placeholder="Editorial"
                                                    title={data.publisher || ''}
                                                    maxLength={FIELD_LIMITS.books.publisher}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ))}
                </div>

                <div className={styles.fetchReviewFooter}>
                    <button className="btn-text" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="btn-primary" onClick={handleApply}>
                        Aplicar cambios seleccionados ({stats.selected})
                    </button>
                </div>
            </div>
        </div>
    )
}
